import { paths } from "@/app/paths"
import { updateCompatiblePropertyTypes } from "@/utils/filterHelpers"
import {
  isSoldOnlyStatusFilter,
  parseStandardStatusParam,
} from "@/utils/resolveSrpPropertySearchEndpoint"

export type SrpListingIntent = "sale" | "rent" | "sold"
export type PropertySaleType = "Sale" | "Rent"
/** Top-level listing type control on SRP filters (For Sale / Sold / For Rent). */
export type SrpListingTypeFilterValue = "Sale" | "Rent" | "Sold"

type SearchParamsLike = Pick<URLSearchParams, "get" | "toString" | "entries">

export function isMapSearchPathname(pathname: string): boolean {
  return paths.isMapSearchPath(pathname)
}

export function isSoldMapSearchPathname(pathname: string): boolean {
  return pathname.startsWith(paths.soldBase())
}

export function resolvePropertySaleTypeFromPathname(
  pathname: string
): PropertySaleType | undefined {
  if (pathname.startsWith(paths.rentBase())) {
    return "Rent"
  }
  if (
    pathname.startsWith(paths.srpBase()) ||
    pathname.startsWith(paths.soldBase())
  ) {
    return "Sale"
  }
  return undefined
}

export function extractLocationSlugFromMapSearchPath(
  pathname: string
): string | null {
  if (
    pathname === paths.srpBase() ||
    pathname === paths.rentBase() ||
    pathname === paths.soldBase()
  ) {
    return "homes"
  }

  const salePrefix = `${paths.srpBase()}/`
  const rentPrefix = `${paths.rentBase()}/`
  const soldPrefix = `${paths.soldBase()}/`

  if (pathname.startsWith(salePrefix)) {
    const slug = pathname.slice(salePrefix.length).split("/")[0]
    return slug && slug.length > 0 ? slug : null
  }

  if (pathname.startsWith(rentPrefix)) {
    const slug = pathname.slice(rentPrefix.length).split("/")[0]
    return slug && slug.length > 0 ? slug : null
  }

  if (pathname.startsWith(soldPrefix)) {
    const slug = pathname.slice(soldPrefix.length).split("/")[0]
    return slug && slug.length > 0 ? slug : null
  }

  return null
}

/** True when listing filters beyond the default sale path are applied. */
export function hasAppliedListingFilters(input: {
  propertySaleType: PropertySaleType | undefined
  standardStatus: string[]
  pathname?: string
}): boolean {
  if (input.pathname && isSoldMapSearchPathname(input.pathname)) {
    return true
  }
  return (
    input.propertySaleType === "Rent" || input.standardStatus.length > 0
  )
}

export function buildMapSearchPath(
  slug: string,
  intent: SrpListingIntent
): string {
  if (intent === "rent") {
    return `${paths.rentBase()}/${slug}`
  }
  if (intent === "sold") {
    return paths.sold(slug)
  }
  return paths.srp(slug)
}

/** Near-me search drops the location slug but keeps sale / rent / sold path intent. */
export function resolveSrpHomesPath(pathname: string): string {
  return buildMapSearchPath("homes", listingIntentFromPathname(pathname))
}

export function listingIntentFromPathname(pathname: string): SrpListingIntent {
  if (isSoldMapSearchPathname(pathname)) {
    return "sold"
  }
  return resolvePropertySaleTypeFromPathname(pathname) === "Rent" ? "rent" : "sale"
}

/** Rewrites an SRP href so listing intent matches the user's current map-search context. */
export function rewriteMapSearchLinkWithListingIntent(
  currentPathname: string,
  href: string
): string {
  if (!isMapSearchPathname(currentPathname)) return href

  let parsed: URL
  try {
    parsed = new URL(href, "https://placeholder.local")
  } catch {
    return href
  }

  const slug = extractLocationSlugFromMapSearchPath(parsed.pathname)
  if (!slug) return href

  const nextPath = buildMapSearchPath(
    slug,
    listingIntentFromPathname(currentPathname)
  )
  const params = new URLSearchParams(parsed.search)
  params.delete("propertySaleType")
  const query = params.toString()
  return query.length > 0 ? `${nextPath}?${query}` : nextPath
}

function listingIntentFromSaleType(target: PropertySaleType): SrpListingIntent {
  return target === "Rent" ? "rent" : "sale"
}

function applyListingTypeSwitchSideEffects(
  params: URLSearchParams,
  target: PropertySaleType
): void {
  params.delete("propertySaleType")
  params.delete("savedSearchName")
  params.delete("priceGte")
  params.delete("priceLte")

  if (target === "Rent") {
    params.delete("standardStatus")
    const propertyTypes =
      params.get("propertyType")?.split(",").filter(Boolean) ?? []
    updateCompatiblePropertyTypes(params, propertyTypes, "Rent")
    return
  }

  const propertyTypes =
    params.get("propertyType")?.split(",").filter(Boolean) ?? []
  updateCompatiblePropertyTypes(params, propertyTypes, "Sale")
}

export function buildListingTypeNavigationUrl(input: {
  pathname: string
  searchParams: SearchParamsLike
  target: PropertySaleType
}): string {
  const slug = extractLocationSlugFromMapSearchPath(input.pathname)
  if (!slug) {
    return input.pathname
  }
  const nextPath = buildMapSearchPath(
    slug,
    listingIntentFromSaleType(input.target)
  )
  const params = new URLSearchParams(input.searchParams.toString())
  applyListingTypeSwitchSideEffects(params, input.target)

  const query = params.toString()
  return query.length > 0 ? `${nextPath}?${query}` : nextPath
}

export function resolveListingTypeFilterValue(input: {
  pathname: string
  standardStatus: string[]
  propertySaleType?: PropertySaleType
}): SrpListingTypeFilterValue {
  if (isSoldMapSearchPathname(input.pathname)) {
    return "Sold"
  }
  if (isSoldOnlyStatusFilter(input.standardStatus)) {
    return "Sold"
  }
  const saleType =
    input.propertySaleType ?? resolvePropertySaleTypeFromPathname(input.pathname)
  if (saleType === "Rent") {
    return "Rent"
  }
  return "Sale"
}

export function buildListingTypeFilterNavigationUrl(input: {
  pathname: string
  searchParams: SearchParamsLike
  target: SrpListingTypeFilterValue
}): string {
  if (input.target === "Rent") {
    return buildListingTypeNavigationUrl({
      pathname: input.pathname,
      searchParams: input.searchParams,
      target: "Rent",
    })
  }

  const slug = extractLocationSlugFromMapSearchPath(input.pathname)
  if (!slug) {
    return input.pathname
  }

  const params = new URLSearchParams(input.searchParams.toString())
  applyListingTypeSwitchSideEffects(params, "Sale")
  params.delete("standardStatus")

  const intent: SrpListingIntent = input.target === "Sold" ? "sold" : "sale"
  const nextPath = buildMapSearchPath(slug, intent)

  const query = params.toString()
  return query.length > 0 ? `${nextPath}?${query}` : nextPath
}

/**
 * Opens a tenant's segregated property type view (e.g. maine's Commercial
 * Lease) on the current location's for-sale or for-rent path, matching the
 * type's sale type. It is a query param, not a new route.
 */
export function buildSegregatedPropertyTypeNavigationUrl(input: {
  pathname: string
  searchParams: SearchParamsLike
  propertyType: string
  propertySaleType: PropertySaleType
}): string {
  const slug = extractLocationSlugFromMapSearchPath(input.pathname)
  if (!slug) {
    return input.pathname
  }

  const params = new URLSearchParams(input.searchParams.toString())
  params.delete("standardStatus")
  params.delete("propertySaleType")
  params.set("propertyType", input.propertyType)

  const intent: SrpListingIntent =
    input.propertySaleType === "Rent" ? "rent" : "sale"
  return `${buildMapSearchPath(slug, intent)}?${params.toString()}`
}

function buildCanonicalSoldRedirect(
  pathname: string,
  searchParams: SearchParamsLike
): string | null {
  const slug = extractLocationSlugFromMapSearchPath(pathname)
  if (!slug) {
    return null
  }

  const params = new URLSearchParams(searchParams.toString())
  params.delete("standardStatus")
  params.delete("propertySaleType")
  const query = params.toString()
  const nextPath = buildMapSearchPath(slug, "sold")
  return query.length > 0 ? `${nextPath}?${query}` : nextPath
}

export function resolveLegacyListingTypeRedirect(
  pathname: string,
  searchParams: SearchParamsLike
): string | null {
  const saleType = searchParams.get("propertySaleType")
  const standardStatus = parseStandardStatusParam(searchParams)

  const slug = extractLocationSlugFromMapSearchPath(pathname)
  if (!slug) {
    return null
  }

  if (
    pathname.startsWith(paths.srpBase()) &&
    isSoldOnlyStatusFilter(standardStatus)
  ) {
    return buildCanonicalSoldRedirect(pathname, searchParams)
  }

  if (
    pathname.startsWith(paths.rentBase()) &&
    isSoldOnlyStatusFilter(standardStatus)
  ) {
    return buildCanonicalSoldRedirect(pathname, searchParams)
  }

  if (pathname.startsWith(paths.soldBase())) {
    if (saleType === "Rent") {
      const params = new URLSearchParams(searchParams.toString())
      params.delete("propertySaleType")
      params.delete("standardStatus")
      const query = params.toString()
      const nextPath = buildMapSearchPath(slug, "rent")
      return query.length > 0 ? `${nextPath}?${query}` : nextPath
    }

    if (saleType === "Sale" && !isSoldOnlyStatusFilter(standardStatus)) {
      const params = new URLSearchParams(searchParams.toString())
      params.delete("propertySaleType")
      params.delete("standardStatus")
      const query = params.toString()
      const nextPath = buildMapSearchPath(slug, "sale")
      return query.length > 0 ? `${nextPath}?${query}` : nextPath
    }

    if (standardStatus.length > 0) {
      const params = new URLSearchParams(searchParams.toString())
      params.delete("standardStatus")
      params.delete("propertySaleType")
      const query = params.toString()
      const nextPath = buildMapSearchPath(slug, "sold")
      return query.length > 0 ? `${nextPath}?${query}` : nextPath
    }

    return null
  }

  if (pathname.startsWith(paths.srpBase()) && saleType === "Rent") {
    const params = new URLSearchParams(searchParams.toString())
    params.delete("propertySaleType")
    params.delete("standardStatus")
    const query = params.toString()
    const nextPath = buildMapSearchPath(slug, "rent")
    return query.length > 0 ? `${nextPath}?${query}` : nextPath
  }

  if (pathname.startsWith(paths.rentBase()) && saleType === "Sale") {
    const params = new URLSearchParams(searchParams.toString())
    params.delete("propertySaleType")
    const query = params.toString()
    const nextPath = buildMapSearchPath(slug, "sale")
    return query.length > 0 ? `${nextPath}?${query}` : nextPath
  }

  return null
}
